// components/building/LocationMap.tsx
/**
 * @mockup /design/mockups/01-mobile-building-landing.png
 * @description Building location with Google Maps embed
 * Design System: Warm Sophistication - rounded corners, warm overlays
 */
import { MapPin, ExternalLink } from 'lucide-react';
import type { Building } from '../../types/database';

interface LocationMapProps {
  building: Building;
}

export function LocationMap({ building }: LocationMapProps) {
  const { latitude, longitude, address, city } = building;

  // If we have coordinates, show embedded map
  const hasCoordinates = latitude && longitude;

  // Google Maps URLs
  const mapsSearchUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${address}, ${city}`
  )}`;

  // For embed, we use the place embed (doesn't require API key for basic usage)
  const embedUrl = hasCoordinates
    ? `https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d2000!2d${longitude}!3d${latitude}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sca`
    : null;

  return (
    <section className="pb-8">
      <h2 className="font-display text-display-sm text-text mb-4">Location</h2>

      <div className="relative w-full aspect-video bg-bg-subtle rounded-lg border border-border overflow-hidden">
        {embedUrl ? (
          // Real Google Maps embed
          <iframe
            src={embedUrl}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title={`Map of ${address}`}
          />
        ) : (
          // Fallback: Placeholder with link
          <>
            {/* Abstract Map Placeholder Pattern */}
            <div className="absolute inset-0 opacity-10 bg-[radial-gradient(var(--color-border-strong)_1px,transparent_1px)] [background-size:16px_16px]" />

            {/* Map Center Marker */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-primary/20 rounded-full animate-ping" />
                <MapPin size={48} className="text-primary relative z-10 fill-current" />
              </div>
            </div>
          </>
        )}

        {/* Map Overlay Badge */}
        <a
          href={mapsSearchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute bottom-3 left-3 bg-surface/95 backdrop-blur-sm px-3 py-2 rounded-md border border-border flex items-center gap-2 hover:bg-surface transition-colors duration-fast"
        >
          <p className="text-body-sm font-medium text-text">{address}</p>
          <ExternalLink size={14} className="text-text-muted" />
        </a>
      </div>
    </section>
  );
}
